/**
 * Calendar and Scheduling API Service
 * Handles calendar events, meetings, availability, and scheduling
 */

import axios from 'axios';
import { config } from '@/src/config';

const API_URL = config.weaveinApi.baseUrl;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type EventType =
  | 'appointment'
  | 'meeting'
  | 'block'
  | 'reminder'
  | 'out_of_office'
  | 'task'
  | 'follow_up'
  // Added for the Somos / VYC FUERTES workstream (backend enum extended in
  // app/schemas/calendar.py:15-24). Hub Meetings track Somos staff attendance
  // at VYC Regional Hub events; Training/Workshop tracks promotora attendance
  // at VYC-hosted training.
  | 'network_hub_meeting'
  | 'training_workshop';
export type LocationType = 'in_person' | 'virtual' | 'phone' | 'hybrid';
export type EventStatus = 'scheduled' | 'confirmed' | 'tentative' | 'cancelled' | 'completed' | 'no_show' | 'rescheduled' | 'archived';

/** Statuses that freeze the event — no edits allowed by the backend. The
 *  EventDetailPage mirrors this set to gate its edit-mode toggle. */
export const TERMINAL_EVENT_STATUSES: ReadonlyArray<EventStatus> = [
  'cancelled',
  'completed',
  'archived',
  'no_show',
];

export function isEventFrozen(status?: EventStatus | null): boolean {
  return !!status && (TERMINAL_EVENT_STATUSES as ReadonlyArray<string>).includes(status);
}
export type EventPriority = 'low' | 'normal' | 'high' | 'urgent';
// 'twilio' = WeaveIn in-app video: the event's link is the WeaveIn join page
// ({origin}/visit/{event_id}), never a public meeting room (TRUS-688).
export type MeetingProvider = 'twilio' | 'zoom' | 'google_meet' | 'teams' | 'jitsi' | 'webex' | 'phone';
export type AttendeeRole = 'organizer' | 'required' | 'optional' | 'attendee' | 'resource';
export type ResponseStatus = 'pending' | 'accepted' | 'declined' | 'tentative' | 'no_response';
export type ReminderType = 'email' | 'sms' | 'push' | 'in_app' | 'whatsapp' | 'voice';
export type OverrideType = 'unavailable' | 'custom_hours' | 'holiday' | 'vacation' | 'sick' | 'training';
export type SyncDirection = 'inbound' | 'outbound' | 'bidirectional';
export type CalendarProvider = 'google' | 'microsoft' | 'ical';

export interface Attendee {
  id?: number;
  event_id?: number;
  user_id?: number;
  member_id?: number;
  external_email?: string;
  external_name?: string;
  attendee_role: AttendeeRole;
  is_optional: boolean;
  response_status: ResponseStatus;
  responded_at?: string;
  response_comment?: string;
  notification_sent: boolean;
  reminder_sent: boolean;
  created_at?: string;
  attendee_name?: string;
  attendee_email?: string;
}

export interface Reminder {
  id?: number;
  event_id?: number;
  reminder_type: ReminderType;
  minutes_before: number;
  scheduled_for?: string;
  sent_at?: string;
  status?: string;
  error_message?: string;
  custom_message?: string;
  target_user_id?: number;
  target_member_id?: number;
  created_at?: string;
}

export interface CalendarMeetingInvitee {
  kind: 'staff' | 'member' | 'external';
  name: string | null;
  /** External guests only; masked unless you are the host or an org admin. */
  email: string | null;
  rsvp: 'pending' | 'accepted' | 'declined' | 'tentative';
}

export interface CalendarEventMeeting {
  id: number | null;
  visible: boolean;
  title?: string;
  status?: string;
  mode?: 'audio' | 'video';
  i_am_host?: boolean;
  can_edit?: boolean;
  can_join_now?: boolean;
  /** In-app path of the meeting card (/workspace-messaging?meeting=N). */
  path?: string;
  provider_label: string;
  invitees?: CalendarMeetingInvitee[];
}

export interface MeetingProviderOption {
  value: MeetingProvider;
  label: string;
  is_default: boolean;
}

export interface MeetingProviderOptions {
  default: MeetingProvider;
  options: MeetingProviderOption[];
}

export interface CalendarEvent {
  id?: number;
  cbo_id?: number;
  title: string;
  description?: string;
  event_type: EventType;

  start_time: string;
  end_time: string;
  all_day: boolean;
  timezone: string;

  location_type: LocationType;
  location_address?: string;
  location_notes?: string;

  meeting_provider?: MeetingProvider;
  meeting_link?: string;
  meeting_id?: string;
  meeting_password?: string;
  meeting_dial_in?: string;

  organizer_id?: number;
  member_id?: number;

  status: EventStatus;
  priority: EventPriority;
  is_private: boolean;
  color?: string;
  tags?: string[];
  custom_fields?: Record<string, any>;

  is_recurring: boolean;
  recurrence_rule?: string;
  recurrence_end_date?: string;
  parent_event_id?: number;

  external_calendar_id?: string;
  external_calendar_provider?: string;
  last_synced_at?: string;
  sync_status?: string;

  cancelled_at?: string;
  cancelled_by?: number;
  cancellation_reason?: string;

  created_at?: string;
  updated_at?: string;
  created_by?: number;

  // TRUS-694: a TRUS-691 meeting's entry. `visible: false` means the viewer may
  // not see the meeting (generic title only); otherwise the real view.
  meeting?: CalendarEventMeeting | null;

  // Computed fields
  organizer_name?: string;
  member_name?: string;
  duration_minutes?: number;
  attendee_count?: number;
  confirmed_count?: number;

  // Related data
  attendees?: Attendee[];
  reminders?: Reminder[];
  /** TRUS-697: set on each occurrence of a recurring series (its start, UTC). */
  occurrence_start?: string | null;
}

export interface CalendarEventCreate {
  title: string;
  description?: string;
  event_type?: EventType;
  start_time: string;
  end_time: string;
  all_day?: boolean;
  timezone?: string;
  location_type?: LocationType;
  location_address?: string;
  location_notes?: string;
  meeting_provider?: MeetingProvider;
  member_id?: number;
  priority?: EventPriority;
  is_private?: boolean;
  color?: string;
  tags?: string[];
  custom_fields?: Record<string, any>;
  is_recurring?: boolean;
  recurrence_rule?: string;
  recurrence_end_date?: string;
  attendees?: AttendeeCreate[];
  reminders?: ReminderCreate[];
  create_meeting_link?: boolean;
  allow_overlap?: boolean;  // Allow overlapping events (default: true in backend)
}

export interface CalendarEventUpdate {
  title?: string;
  description?: string;
  event_type?: EventType;
  start_time?: string;
  end_time?: string;
  all_day?: boolean;
  timezone?: string;
  location_type?: LocationType;
  location_address?: string;
  location_notes?: string;
  meeting_provider?: MeetingProvider;
  meeting_link?: string;
  status?: EventStatus;
  priority?: EventPriority;
  is_private?: boolean;
  color?: string;
  tags?: string[];
  custom_fields?: Record<string, any>;
  cancellation_reason?: string;
}

export interface AttendeeCreate {
  user_id?: number;
  member_id?: number;
  external_email?: string;
  external_name?: string;
  attendee_role?: AttendeeRole;
  is_optional?: boolean;
}

export interface AttendeeUpdate {
  response_status?: ResponseStatus;
  response_comment?: string;
  is_optional?: boolean;
}

export interface ReminderCreate {
  reminder_type: ReminderType;
  minutes_before: number;
  custom_message?: string;
  target_user_id?: number;
  target_member_id?: number;
}

export interface AvailabilitySlot {
  id?: number;
  user_id?: number;
  cbo_id?: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  timezone: string;
  is_active: boolean;
  slot_duration_minutes: number;
  buffer_minutes: number;
  max_appointments_per_slot: number;
  default_location_type: LocationType;
  default_location_address?: string;
  effective_from?: string;
  effective_until?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AvailabilityOverride {
  id?: number;
  user_id?: number;
  cbo_id?: number;
  override_date: string;
  override_type: OverrideType;
  start_time?: string;
  end_time?: string;
  reason?: string;
  is_recurring?: boolean;
  recurrence_end_date?: string;
  created_at?: string;
  created_by?: number;
}

export interface StaffAvailability {
  user_id: number;
  user_name: string;
  timezone: string;
  weekly_schedule: AvailabilitySlot[];
  overrides: AvailabilityOverride[];
}

export interface FreeSlot {
  start_time: string;
  end_time: string;
  duration_minutes: number;
  is_available: boolean;
  location_type?: LocationType;
}

export interface FreeSlotsResponse {
  user_id: number;
  date: string;
  timezone: string;
  slots: FreeSlot[];
}

export interface MeetingProviderConfig {
  id?: number;
  cbo_id?: number;
  provider_type: MeetingProvider;
  provider_name?: string;
  is_enabled: boolean;
  is_default: boolean;
  jitsi_server_url?: string;
  config?: Record<string, any>;
  zoom_account_id?: string;
  meetings_created?: number;
  last_used_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface MeetingInfo {
  provider: MeetingProvider;
  meeting_id: string;
  join_url: string;
  password?: string;
  dial_in?: string;
  host_url?: string;
  start_url?: string;
}

export interface CreateMeetingRequest {
  provider?: MeetingProvider;
  title: string;
  start_time: string;
  duration_minutes?: number;
  attendee_emails?: string[];
}

export interface EventFilters {
  start_date?: string;
  end_date?: string;
  user_id?: number;
  member_id?: number;
  event_type?: EventType;
  status_filter?: EventStatus;
  include_cancelled?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
  /** TRUS-695: the IANA zone start_date / end_date are days in (default: the user's own). */
  tz?: string;
}

export interface CalendarEventListResponse {
  events: CalendarEvent[];
  total: number;
  page: number;
  page_size: number;
}

// Status and type labels for display
export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  tentative: 'Tentative',
  cancelled: 'Cancelled',
  completed: 'Completed',
  no_show: 'No Show',
  rescheduled: 'Rescheduled',
};

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  appointment: 'Appointment',
  meeting: 'Meeting',
  block: 'Block Time',
  reminder: 'Reminder',
  out_of_office: 'Out of Office',
  task: 'Task',
  follow_up: 'Follow Up',
  network_hub_meeting: 'Hub Meeting',
  training_workshop: 'Training / Workshop',
};

export const LOCATION_TYPE_LABELS: Record<LocationType, string> = {
  in_person: 'In Person',
  virtual: 'Virtual',
  phone: 'Phone',
  hybrid: 'Hybrid',
};

export const MEETING_PROVIDER_LABELS: Record<MeetingProvider, string> = {
  twilio: 'WeaveIn Video',
  zoom: 'Zoom',
  google_meet: 'Google Meet',
  teams: 'Microsoft Teams',
  jitsi: 'Jitsi Meet',
  webex: 'Webex',
  phone: 'Phone Call',
};

export const PRIORITY_LABELS: Record<EventPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

export const RESPONSE_STATUS_LABELS: Record<ResponseStatus, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  declined: 'Declined',
  tentative: 'Tentative',
  no_response: 'No Response',
};

export const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// ============================================================================
// API HELPER
// ============================================================================

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'X-CBO-ID': cboId || '',
    'Content-Type': 'application/json',
  };
};

// ============================================================================
// CALENDAR EVENTS API
// ============================================================================

export const calendarAPI = {
  // Get events with filters
  getEvents: async (filters: EventFilters = {}): Promise<CalendarEventListResponse> => {
    const params = new URLSearchParams();
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.tz) params.append('tz', filters.tz);
    if (filters.user_id) params.append('user_id', filters.user_id.toString());
    if (filters.member_id) params.append('member_id', filters.member_id.toString());
    if (filters.event_type) params.append('event_type', filters.event_type);
    if (filters.status_filter) params.append('status_filter', filters.status_filter);
    if (filters.include_cancelled !== undefined) params.append('include_cancelled', filters.include_cancelled.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.page_size) params.append('page_size', filters.page_size.toString());

    const response = await axios.get(`${API_URL}/api/calendar/events?${params}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Get single event with details
  getEvent: async (eventId: number): Promise<CalendarEvent> => {
    const response = await axios.get(`${API_URL}/api/calendar/events/${eventId}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Create new event
  createEvent: async (eventData: CalendarEventCreate): Promise<CalendarEvent> => {
    const response = await axios.post(`${API_URL}/api/calendar/events`, eventData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Update event
  updateEvent: async (eventId: number, eventData: CalendarEventUpdate): Promise<CalendarEvent> => {
    const response = await axios.put(`${API_URL}/api/calendar/events/${eventId}`, eventData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Delete event
  deleteEvent: async (eventId: number, notifyAttendees: boolean = true): Promise<void> => {
    await axios.delete(`${API_URL}/api/calendar/events/${eventId}?notify_attendees=${notifyAttendees}`, {
      headers: getAuthHeaders(),
    });
  },

  // Get my events (current user)
  getMyEvents: async (filters: EventFilters = {}): Promise<CalendarEventListResponse> => {
    const params = new URLSearchParams();
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.tz) params.append('tz', filters.tz);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.page_size) params.append('page_size', filters.page_size.toString());

    const response = await axios.get(`${API_URL}/api/calendar/my/events?${params}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Get member events
  getMemberEvents: async (memberId: number, filters: EventFilters = {}): Promise<CalendarEventListResponse> => {
    const params = new URLSearchParams();
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.tz) params.append('tz', filters.tz);
    if (filters.include_past !== undefined) params.append('include_past', (filters as any).include_past.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.page_size) params.append('page_size', filters.page_size.toString());

    const response = await axios.get(`${API_URL}/api/calendar/member/${memberId}/events?${params}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },
};

// ============================================================================
// ATTENDEE API
// ============================================================================

export const attendeeAPI = {
  // Add attendee to event
  addAttendee: async (eventId: number, attendee: AttendeeCreate): Promise<Attendee> => {
    const response = await axios.post(`${API_URL}/api/calendar/events/${eventId}/attendees`, attendee, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Update attendee response
  respondToEvent: async (attendeeId: number, response: AttendeeUpdate): Promise<Attendee> => {
    const res = await axios.put(`${API_URL}/api/calendar/attendees/${attendeeId}/respond`, response, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  // Remove attendee
  removeAttendee: async (attendeeId: number): Promise<void> => {
    await axios.delete(`${API_URL}/api/calendar/attendees/${attendeeId}`, {
      headers: getAuthHeaders(),
    });
  },

  // Mark complete — freezes the event (no further field edits allowed by the BE).
  completeEvent: async (eventId: number): Promise<CalendarEvent> => {
    const res = await axios.post(
      `${API_URL}/api/calendar/events/${eventId}/complete`,
      {},
      { headers: getAuthHeaders() },
    );
    return res.data;
  },

  // Archive — hides from default list views. BE requires status to already be
  // 'completed' or 'cancelled' (returns 409 otherwise).
  archiveEvent: async (eventId: number): Promise<CalendarEvent> => {
    const res = await axios.post(
      `${API_URL}/api/calendar/events/${eventId}/archive`,
      {},
      { headers: getAuthHeaders() },
    );
    return res.data;
  },
};

// ============================================================================
// AVAILABILITY API
// ============================================================================

export const availabilityAPI = {
  // Get staff availability
  getAvailability: async (userId: number): Promise<StaffAvailability> => {
    const response = await axios.get(`${API_URL}/api/calendar/availability/${userId}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Get my availability
  getMyAvailability: async (): Promise<StaffAvailability> => {
    const response = await axios.get(`${API_URL}/api/calendar/my/availability`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Create availability slot
  createSlot: async (slot: Omit<AvailabilitySlot, 'id' | 'user_id' | 'cbo_id' | 'created_at' | 'updated_at'>): Promise<AvailabilitySlot> => {
    const response = await axios.post(`${API_URL}/api/calendar/availability`, slot, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Update availability slot
  updateSlot: async (slotId: number, slot: Partial<AvailabilitySlot>): Promise<AvailabilitySlot> => {
    const response = await axios.put(`${API_URL}/api/calendar/availability/${slotId}`, slot, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Delete availability slot
  deleteSlot: async (slotId: number): Promise<void> => {
    await axios.delete(`${API_URL}/api/calendar/availability/${slotId}`, {
      headers: getAuthHeaders(),
    });
  },

  // Create availability override
  createOverride: async (override: Omit<AvailabilityOverride, 'id' | 'user_id' | 'cbo_id' | 'created_at' | 'created_by'>): Promise<AvailabilityOverride> => {
    const response = await axios.post(`${API_URL}/api/calendar/availability/overrides`, override, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Get free slots for a user on a date
  getFreeSlots: async (userId: number, date: string, durationMinutes: number = 30): Promise<FreeSlotsResponse> => {
    const response = await axios.get(
      `${API_URL}/api/calendar/free-slots/${userId}?target_date=${date}&duration_minutes=${durationMinutes}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
};

// ============================================================================
// MEETING PROVIDERS API
// ============================================================================

export const meetingAPI = {
  // List configured providers
  getProviders: async (): Promise<MeetingProviderConfig[]> => {
    const response = await axios.get(`${API_URL}/api/calendar/meeting-providers`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /** What the event form may offer: WeaveIn video always, Jitsi only for a private server (TRUS-694). */
  getProviderOptions: async (): Promise<MeetingProviderOptions> => {
    const response = await axios.get(`${API_URL}/api/calendar/meeting-provider-options`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Configure provider
  createProvider: async (provider: Omit<MeetingProviderConfig, 'id' | 'cbo_id' | 'created_at' | 'updated_at'>): Promise<MeetingProviderConfig> => {
    const response = await axios.post(`${API_URL}/api/calendar/meeting-providers`, provider, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Create standalone meeting link
  createMeeting: async (request: CreateMeetingRequest): Promise<MeetingInfo> => {
    const response = await axios.post(`${API_URL}/api/calendar/meetings/create`, request, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /** What the WeaveIn join page should do for this event (staff login; 404 otherwise). */
  getEventJoinInfo: async (eventId: number): Promise<EventJoinInfo> => {
    const response = await axios.get(`${API_URL}/api/calendar/events/${eventId}/join`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /** A short-lived video token for a WeaveIn-hosted staff meeting (organizer / attendees only). */
  getEventVideoToken: async (eventId: number): Promise<EventVideoToken> => {
    const response = await axios.post(`${API_URL}/api/calendar/events/${eventId}/video/token`, {}, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },
};

// ============================================================================
// WEAVEIN JOIN LINKS (TRUS-688)
// ============================================================================

export interface EventJoinInfo {
  event_id: number;
  title?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  /** telehealth: open the visit; room: in-app WeaveIn meeting; external: a configured provider's link */
  kind: 'telehealth' | 'room' | 'external';
  telehealth_session_id?: number | null;
  join_url?: string | null;
  join_opens_at?: string | null;
  join_closes_at?: string | null;
  can_join_now: boolean;
  is_participant: boolean;
}

export interface EventVideoToken {
  token: string;
  room_name: string;
  identity: string;
  expires_in: number;
}

/** The event id when `url` is a WeaveIn join link ({origin}/visit/{id}), else null. */
export function weaveInJoinEventId(url?: string | null): number | null {
  if (!url) return null;
  const m = /\/visit\/(\d+)\/?(?:[?#].*)?$/.exec(url.trim());
  return m ? Number(m[1]) : null;
}

/** A legacy public meet.jit.si room (replaced by WeaveIn join links in TRUS-688). */
export function isPublicJitsiLink(url?: string | null): boolean {
  if (!url) return false;
  try {
    const host = new URL(url.trim()).hostname.toLowerCase();
    return host === 'meet.jit.si' || host === 'www.meet.jit.si';
  } catch {
    return false;
  }
}

/** True for a configured provider's link (Zoom, Meet, Teams, private Jitsi) that opens outside WeaveIn. */
export function isExternalMeetingLink(url?: string | null): boolean {
  return !!url && weaveInJoinEventId(url) === null && !isPublicJitsiLink(url);
}

/** The in-app path for a WeaveIn join link, so it opens in this app rather than a hard-coded host. */
export function weaveInJoinPath(url?: string | null): string | null {
  const id = weaveInJoinEventId(url);
  return id ? `/visit/${id}` : null;
}

// Export default for convenience
export default {
  calendar: calendarAPI,
  attendee: attendeeAPI,
  availability: availabilityAPI,
  meeting: meetingAPI,
};
